import { Helmet, HelmetProvider } from 'react-helmet-async';

interface SEOProps {
  title?: string;
  description?: string;
  keywords?: string;
  image?: string;
  url?: string;
  type?: 'website' | 'article' | 'product';
  noIndex?: boolean;
  structuredData?: object;
}

const defaultSEO = {
  siteName: 'ThinkWay',
  title: 'ThinkWay - Planejamento Estratégico com Inteligência Artificial',
  description: 'ThinkWay é a plataforma de planejamento estratégico empresarial com IA. Defina metas, crie planos de ação, faça análise SWOT e acompanhe resultados em tempo real. Teste grátis por 7 dias.',
  keywords: 'planejamento estratégico, gestão empresarial, plano de ação, metas empresariais, análise SWOT, OKR, KPI, inteligência artificial, gestão de metas, dashboard empresarial',
  image: 'https://thinkway.app/screenshots/og-image.png',
  url: 'https://thinkway.app',
  type: 'website' as const,
};

export const SEOProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  return <HelmetProvider>{children}</HelmetProvider>;
};

const SEO: React.FC<SEOProps> = ({
  title,
  description = defaultSEO.description,
  keywords = defaultSEO.keywords,
  image = defaultSEO.image,
  url = defaultSEO.url,
  type = defaultSEO.type,
  noIndex = false,
  structuredData,
}) => {
  const fullTitle = title 
    ? `${title} | ${defaultSEO.siteName}`
    : defaultSEO.title;

  return (
    <Helmet>
      {/* Primary Meta Tags */}
      <title>{fullTitle}</title>
      <meta name="title" content={fullTitle} />
      <meta name="description" content={description} />
      <meta name="keywords" content={keywords} />
      
      {/* Robots */}
      {noIndex ? (
        <meta name="robots" content="noindex, nofollow" />
      ) : (
        <meta name="robots" content="index, follow" />
      )}

      {/* Canonical URL */}
      <link rel="canonical" href={url} />

      {/* Open Graph / Facebook */}
      <meta property="og:type" content={type} />
      <meta property="og:url" content={url} />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={description} />
      <meta property="og:image" content={image} />
      <meta property="og:site_name" content={defaultSEO.siteName} />
      <meta property="og:locale" content="pt_BR" />

      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:url" content={url} />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={image} />

      {/* Structured Data */}
      {structuredData && (
        <script type="application/ld+json">
          {JSON.stringify(structuredData)}
        </script>
      )}
    </Helmet>
  );
};

export default SEO;

// Predefined SEO configurations for common pages
export const SEOConfigs = {
  landing: {
    title: undefined, // Uses default
    description: defaultSEO.description,
    url: 'https://thinkway.app',
  },
  login: {
    title: 'Login',
    description: 'Acesse sua conta ThinkWay para gerenciar seu planejamento estratégico empresarial.',
    url: 'https://thinkway.app/tw/login',
    noIndex: true,
  },
  register: {
    title: 'Cadastro - Teste Grátis por 7 Dias',
    description: 'Crie sua conta ThinkWay e comece a usar a plataforma de planejamento estratégico com IA. Teste grátis por 7 dias sem cartão de crédito.',
    url: 'https://thinkway.app/tw/register',
  },
  dashboard: {
    title: 'Dashboard',
    description: 'Acompanhe seus indicadores e metas estratégicas em tempo real no dashboard ThinkWay.',
    url: 'https://thinkway.app/tw/dashboard',
    noIndex: true,
  },
  diretrizes: {
    title: 'Diretrizes Estratégicas',
    description: 'Defina a missão, visão e valores da sua empresa com o ThinkWay.',
    url: 'https://thinkway.app/tw/diretrizes',
    noIndex: true,
  },
  metas: {
    title: 'Metas Estratégicas',
    description: 'Gerencie suas metas empresariais e acompanhe o progresso em tempo real.',
    url: 'https://thinkway.app/tw/metas',
    noIndex: true,
  },
  planoDeAcao: {
    title: 'Plano de Ação',
    description: 'Crie e gerencie planos de ação com ajuda de inteligência artificial.',
    url: 'https://thinkway.app/tw/plano-de-acao',
    noIndex: true,
  },
  swot: {
    title: 'Análise SWOT',
    description: 'Realize análise SWOT automatizada da sua empresa com o ThinkWay.',
    url: 'https://thinkway.app/tw/swot',
    noIndex: true,
  },
  pricing: {
    title: 'Planos e Preços',
    description: 'Conheça os planos do ThinkWay. A partir de R$ 49,90/mês. Teste grátis por 7 dias.',
    url: 'https://thinkway.app/#precos',
  },
};
